import { TEXT_LINK_FOCUS_RING } from "../../lib/a11yFocus";
import { currencyLabel } from "../../lib/currency";
import { useEffect, useState } from "react";
import PageHeadings from "../../components/BaseComponents/PageHeadings/PageHeadings";
import PageTemplate from "../../components/BaseComponents/PageTemplate/PageTemplate";
import { Button, InlineAlert, SearchableSelect, SearchInput } from "../../components/BaseComponents";
import { countries } from "../../assets/countryList";
import useApi from "../../hooks/useApi";
import useCleanup from "../../hooks/useCleanup";
import SimpleSortTable, { TableColumn } from "../../components/BaseComponents/SimpleSortTable/SimpleSortTable";
import Pagination from "../../components/BaseComponents/Pagination/Pagination";
import { formatDate, getApiErrorMessage } from "../../lib/utils";
import { Link } from "react-router-dom";
import Label from "../../components/BaseComponents/Label";
import usePagination from "../../hooks/usePagination";
import { useDebouncedFilters } from "../../hooks/useDebouncedFilters";

const columns: TableColumn[] = [
    {
        id: 'companyName',
        label: 'Organisation',
        renderer: (key, data) => (
			<Link className={`text-link ${TEXT_LINK_FOCUS_RING}`} to={`/admin/companies/${data.company.id}`}>
				{data.company.name}
			</Link>
		)
    },
    {
        id: 'companyNumber',
        label: 'Organisation #',
        renderer: (key, data) => data.company.number,
    },
    {
        id: 'companyTier',
        label: 'Tier',
        renderer: (key, data) => data.company.tier,
    },
    {
        id: 'period',
        label: 'Period',
        renderer: (key, data) => (
			<span>
				{ formatDate(data.start_date) }
				{' - '}
				{ formatDate(data.end_date) }
			</span>
		)
    },
    {
        id: 'start_date',
        label: 'Start Date',
        renderer: (key, data) => formatDate(data[key])
    },
    {
        id: 'signed_by',
        label: 'Signed By',
        renderer: (key, data) => (
			<span>{ data.signee_name } - { data.signee_title }</span>
		)
    },
    {
        id: 'user',
        label: 'User',
        renderer: (key, data) => data.user?.full_name,
    },
    {
        id: 'user_email',
        label: 'User Email',
        renderer: (key, data) => data.user?.email,
    },
    {
        id: 'end_date',
        label: 'End Date',
        renderer: (key, data) => formatDate(data[key])
    },
    {
        id: 'view',
        label: 'View',
    },
    {
        id: 'created_at',
        label: 'Created At',
		renderer: (key, data) => formatDate(data[key]),
    },
	{
		id: 'report_type',
		label: 'Report Type',
		renderer: () => 'SECR',
	},
	{
		id: 'publish_status',
		label: 'Publish Status',
		renderer: (key, data) => {
			const value = data[key];
			let label = 'Draft';
			if(value === 'published') {
				label = 'Published';
			}
			return (
				<span>{ label }</span>
			)
		},
	}
]

const reportTypeOptions = [
	{ value: '', label: 'Any' },
	{ value: 'SECR', label: 'SECR' },
]

const tierOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'micro', label: 'Micro' },
	{ value: 'standard', label: 'Standard' },
	{ value: 'premium', label: 'Premium' },
]

const publicBodyOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'yes', label: 'Yes' },
	{ value: 'no', label: 'No' },
]

const setupStatusOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'no', label: 'Not set up' },
	{ value: 'yes', label: 'Set up' },
]

const invitationStatusOptions = [
	{ value: 'any', label: 'Any' },
	{ value: 'None', label: 'None' },
	{ value: 'Sent', label: 'Sent' },
	{ value: 'Accepted', label: 'Accepted' },
]

function AdminReports() {
    const api = useApi();
    const [data, setData] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<any>();

	const pagination = usePagination();

	const [filters, setFilters] = useState({
		signeeName: '',
		signeeTitle: '',
		userFullName: '',
		userEmail: '',
        date: '',
        reportType: '',
		companyName: '',
		number: '',
		type: 'any',
		jurisdiction: 'any',
		publicBody: 'any',
		setupStatus: 'any',
		invitationStatus: 'any',
		tier: 'any',
		currencyCode: 'any',
	})

	const [dynamicFacets, setDynamicFacets] = useState({
		currencyCodes: [] as string[],
		jurisdictions: [] as string[],
		companyTypes: [] as string[],
	});
	const [facetsError, setFacetsError] = useState<any>();

	const companyTypeOptions = [
		{ value: 'any', label: 'Any' },
		...dynamicFacets.companyTypes.map((companyType) => ({
			value: companyType,
			label: companyType || '[unset]',
		})),
	]

	const jurisdictionOptions = [
		{ value: 'any', label: 'Any' },
		...countries
			.filter((country) => dynamicFacets.jurisdictions.includes(country['value']))
			.map((country) => ({ value: country.value, label: country.label })),
	]

	const currencyOptions = [
		{ value: 'any', label: 'Any' },
		...dynamicFacets.currencyCodes.map((currencyCode) => ({
			value: currencyCode,
			label: currencyLabel(currencyCode),
		})),
	]

	// Issue #884: this GET sits behind Auth/RequireWriteAccess and can receive the now-rejecting
	// ACCESS_AUDIT_UNAVAILABLE (#857 item 6), or any other failure, instead of hanging forever (and
	// instead of the unhandled rejection the un-caught `run()` call used to leave behind). It's
	// independent of the main reports list load below, so its own failure - and its own retry -
	// must not affect the main list's data/loading/error state at all. On failure, the dropdown
	// options already on screen (if any) are left exactly as they were rather than being reset to
	// an empty/successful-looking state.
	const loadFacets = useCleanup(() => {
		let cancelled = false;
		const run = async () => {
			const apiResult = await api.get('admin/companiesSearchFacets');
			if(cancelled) {
				return;
			}
			setDynamicFacets(apiResult.data);
			setFacetsError(undefined);
		}
		run().catch((err) => {
			if(cancelled) {
				return;
			}
			setFacetsError(err);
		})

		return () => { cancelled = true }
	})

	useEffect(() => {
		loadFacets()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Issue #884: onSearch previously had no catch at all, so a rejecting GET left `loading` stuck
	// true forever, left the table showing whatever (possibly empty/stale) data it already had as
	// if that were the real result, and produced an unhandled rejection. useCleanup also makes the
	// cancellation flag apply on unmount automatically, since useDebouncedFilters' debounce timer
	// and flush() both discard the cleanup function this used to return.
    const onSearch = useCleanup(() => {
		let cancelled = false;
		setLoading(true);
		const run = async () => {
			const qs = new URLSearchParams({
				...filters,
				...pagination.params,
			})
			const results = await api.get('admin/reports?' + qs.toString());
			if(cancelled) {
				return;
			}
			setData(results.data.reports);
			pagination.readMeta(results.data.meta);
			setLoadError(undefined);
			setLoading(false);
		}
		run().catch((err) => {
			if(cancelled) {
				return;
			}
			setLoading(false);
			setLoadError(err);
		})
		return () => {
			cancelled = true;
		}
    })

	const { searchInProgress, flush: flushSearch } = useDebouncedFilters(filters, onSearch)

    useEffect(() => {
        onSearch()
		// eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.params])

    return (
        <PageTemplate>
            <PageHeadings title="Reports" />
			{facetsError && (
				<InlineAlert type='warning' className='mt-4' data-testid='admin-reports-facets-error'>
					{getApiErrorMessage(facetsError, 'Failed to load filter options. Please try again.')}
					{' '}
					<Button type='Secondary' size='sm' text='Retry facets' additionalClass='ml-2' onClick={() => loadFacets()} />
				</InlineAlert>
			)}
			<div className="text-white mt-8 mb-1">
				Report Filters
			</div>
			<div className="flex space-x-3">
				<div>
					<Label className="block">
						Report type
					</Label>
					<SearchableSelect
						isSearchable={false}
						options={reportTypeOptions}
						defaultValue={reportTypeOptions[0]}
					/>
				</div>
				<SearchInput
					name="signeeName"
					label="Signee Name"
					placeholder="Jo Bloggs..."
					value={filters.signeeName}
					onChange={e => setFilters({...filters, signeeName: e.target.value})}
					onSearch={flushSearch}
					onClear={() => setFilters({...filters, signeeName: ''})}
					isLoading={searchInProgress}
				/>
				<SearchInput
					name="signeeTitle"
					label="Signee Title"
					placeholder="Director..."
					value={filters.signeeTitle}
					onChange={e => setFilters({...filters, signeeTitle: e.target.value})}
					onSearch={flushSearch}
					onClear={() => setFilters({...filters, signeeTitle: ''})}
					isLoading={searchInProgress}
				/>
			</div>
			<div className="text-white mt-4 mb-1">
				User Filters
			</div>
			<div className="flex space-x-3">
				<SearchInput
					name="userFullName"
					label="User Full name"
					placeholder="Jo Bloggs..."
					value={filters.userFullName}
					onChange={e => setFilters({...filters, userFullName: e.target.value})}
					onSearch={flushSearch}
					onClear={() => setFilters({...filters, userFullName: ''})}
					isLoading={searchInProgress}
				/>
				<SearchInput
					name="userEmail"
					label="User Email"
					placeholder="test@neoni-app.com..."
					value={filters.userEmail}
					onChange={e => setFilters({...filters, userEmail: e.target.value})}
					onSearch={flushSearch}
					onClear={() => setFilters({...filters, userEmail: ''})}
					isLoading={searchInProgress}
				/>
			</div>
			<div className="text-white mt-4 mb-1">
				Organisation filters
			</div>
			<div className="flex space-x-3">
				<SearchInput
					name="companyName"
					label="Name"
					placeholder="iSumio..."
					value={filters.companyName}
					onChange={e => setFilters({...filters, companyName: e.target.value})}
					onSearch={flushSearch}
					onClear={() => setFilters({...filters, companyName: ''})}
					isLoading={searchInProgress}
				/>
				<SearchInput
					name="number"
					label="Organisation Number"
					placeholder="123..."
					value={filters.number}
					onChange={e => setFilters({...filters, number: e.target.value})}
					onSearch={flushSearch}
					onClear={() => setFilters({...filters, number: ''})}
					isLoading={searchInProgress}
				/>
				<label>
					<Label className="block">Organisation Type</Label>
					<SearchableSelect
						isSearchable={false}
						options={companyTypeOptions}
						value={companyTypeOptions.find((option) => option.value === filters.type)}
						onChange={(option) => setFilters({...filters, type: option?.value ?? 'any'})}
					/>
				</label>	
				<label>
					<Label className="block">Tier</Label>
					<SearchableSelect
						isSearchable={false}
						options={tierOptions}
						value={tierOptions.find((option) => option.value === filters.tier)}
						onChange={(option) => setFilters({...filters, tier: option?.value ?? 'any'})}
					/>
				</label>
			</div>
			<div className="flex space-x-3 my-3">
				<label>
					<Label className="block">Jurisdiction</Label>
					<SearchableSelect
						options={jurisdictionOptions}
						value={jurisdictionOptions.find((option) => option.value === filters.jurisdiction)}
						onChange={(option) => setFilters({...filters, jurisdiction: option?.value ?? 'any'})}
					/>
				</label>
				<label>
					<Label className="block">Public Body</Label>
					<SearchableSelect
						isSearchable={false}
						options={publicBodyOptions}
						value={publicBodyOptions.find((option) => option.value === filters.publicBody)}
						onChange={(option) => setFilters({...filters, publicBody: option?.value ?? 'any'})}
					/>
				</label>
				<label>
					<Label className="block">Set Up</Label>
					<SearchableSelect
						isSearchable={false}
						options={setupStatusOptions}
						value={setupStatusOptions.find((option) => option.value === filters.setupStatus)}
						onChange={(option) => setFilters({...filters, setupStatus: option?.value ?? 'any'})}
					/>
				</label>

				<label>
					<Label className="block">Invitation Status</Label>
					<SearchableSelect
						isSearchable={false}
						isDisabled
						options={invitationStatusOptions}
						value={invitationStatusOptions.find((option) => option.value === filters.invitationStatus)}
						onChange={(option) => setFilters({...filters, invitationStatus: option?.value ?? 'any'})}
					/>
				</label>

				<label>
					<Label className="block">Currency</Label>
					<SearchableSelect
						options={currencyOptions}
						value={currencyOptions.find((option) => option.value === filters.currencyCode)}
						onChange={(option) => setFilters({...filters, currencyCode: option?.value ?? 'any'})}
					/>
				</label>

			</div>

			<div>
				<Button
					type='Primary'
					text='Search'
					size='sm'
					hasTrailingIcon
					onClick={flushSearch}
					additionalClass='mt-4 mb-2'
				/>
			</div>

			{loadError ? (
				<>
					<InlineAlert type='error' className='mt-4' data-testid='admin-reports-load-error'>
						{getApiErrorMessage(loadError, 'Failed to load reports. Please try again.')}
					</InlineAlert>
					<Button type='Secondary' text='Retry' additionalClass='mt-4' onClick={() => onSearch()} />
				</>
			) : (
				<>
					<SimpleSortTable
						isLoading={loading || searchInProgress}
						additionalClass="mt-4"
						columns={columns}
						data={data}

					/>
					<Pagination
						isLoading={loading}
						pagination={pagination.pagination}
						onPageChange={pagination.onPageChange}
					/>
				</>
			)}
        </PageTemplate>
    )
}

export default AdminReports;
